import { useId } from "react";
import type { DashboardPoint } from "./types";
import { DISPLAY_LOCALE } from "../format/dates";

/*
 * Small SVG charts for the home dashboard. They are drawn here rather than with @how/ui/charts so they take the
 * dashboard's colours through CSS variables (--pv-chart-a, --pv-chart-b, --pv-part-N), and so the home page, the first
 * screen everyone opens, does not pull Recharts in. Each carries its numbers as a text alternative.
 */

type Pt = readonly [number, number];

/** A Catmull-Rom curve through the points, as cubic Béziers: smooth, and it passes through every value. */
function smoothPath(pts: readonly Pt[]): string {
  const first = pts[0];
  if (first === undefined) return "";
  let d = `M${first[0].toFixed(2)},${first[1].toFixed(2)}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p1 = pts[i] ?? first;
    const p0 = pts[i - 1] ?? p1;
    const p2 = pts[i + 1] ?? p1;
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
  }
  return d;
}

/** Plots values into a w × h box, highest at the top, with a little headroom so the line never touches the edge. */
function plot(
  values: readonly number[],
  w: number,
  h: number,
  pad: number,
  fromZero: boolean,
): Pt[] {
  const max = Math.max(...values);
  // A chart with an axis starts at zero; a sparkline shows the shape, so it starts at its own lowest value.
  const min = fromZero ? Math.min(0, ...values) : Math.min(...values);
  const span = max - min || 1;
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  return values.map((v, i) => [i * step, h - pad - ((v - min) / span) * (h - pad * 2)] as const);
}

function useSvgId(prefix: string): string {
  return `${prefix}${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

/** A metric's recent shape. Drawn only from two points up; colour comes from the tile's `color`. */
export function Sparkline({ values, label }: { values: readonly number[]; label: string }) {
  const id = useSvgId("pv-spark");
  if (values.length < 2) return null;
  const pts = plot(values, 100, 36, 3, false);
  const line = smoothPath(pts);
  return (
    <svg
      className="pv-spark"
      viewBox="0 0 100 36"
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.32" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line} L100,36 L0,36 Z`} fill={`url(#${id})`} />
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** An area chart over the trend's points, with the axis labels in HTML so they never stretch with the plot. */
export function TrendChart({ points, unit }: { points: readonly DashboardPoint[]; unit: string }) {
  const fillId = useSvgId("pv-fill");
  const strokeId = useSvgId("pv-stroke");
  const values = points.map((p) => p.value);
  const pts = plot(values, 600, 200, 14, true);
  const line = smoothPath(pts);
  const max = Math.max(0, ...values);
  const last = pts[pts.length - 1];
  const summary = `${unit}: ${points.map((p) => `${p.label} ${String(p.value)}`).join(", ")}`;
  return (
    <div className="pv-trend">
      <div className="pv-trend__axis" aria-hidden>
        <span>{max.toLocaleString(DISPLAY_LOCALE)}</span>
        <span>{Math.round(max / 2).toLocaleString(DISPLAY_LOCALE)}</span>
        <span>0</span>
      </div>
      <div className="pv-trend__plot">
        <div className="pv-trend__canvas">
          <svg viewBox="0 0 600 200" preserveAspectRatio="none" role="img" aria-label={summary}>
            <defs>
              <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0"
                  style={{
                    stopColor: "var(--pv-chart-a)",
                    stopOpacity: "var(--pv-fill-top, 0.34)",
                  }}
                />
                <stop offset="1" style={{ stopColor: "var(--pv-chart-b)", stopOpacity: 0 }} />
              </linearGradient>
              <linearGradient id={strokeId} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" style={{ stopColor: "var(--pv-chart-a)" }} />
                <stop offset="1" style={{ stopColor: "var(--pv-chart-b)" }} />
              </linearGradient>
            </defs>
            {[0, 0.5, 1].map((f) => (
              <line
                key={f}
                className="pv-trend__grid"
                x1="0"
                x2="600"
                y1={14 + f * 172}
                y2={14 + f * 172}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {pts.length > 1 ? (
              <>
                <path d={`${line} L600,200 L0,200 Z`} fill={`url(#${fillId})`} />
                <path
                  className="pv-trend__line"
                  d={line}
                  fill="none"
                  stroke={`url(#${strokeId})`}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </>
            ) : null}
          </svg>
          {last === undefined ? null : (
            <span
              className="pv-trend__dot"
              aria-hidden
              style={{
                left: `${String((last[0] / 600) * 100)}%`,
                top: `${String((last[1] / 200) * 100)}%`,
              }}
            />
          )}
        </div>
        <div className="pv-trend__labels" aria-hidden>
          {points.map((p) => (
            <span key={p.label}>{p.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** A donut of the parts, largest first, with the total in the middle. Colours are --pv-part-0 to --pv-part-4. */
export function Ring({
  parts,
  totalLabel,
}: {
  parts: readonly DashboardPoint[];
  totalLabel: string;
}) {
  const total = parts.reduce((sum, p) => sum + p.value, 0);
  const r = 42;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const summary = parts.map((p) => `${p.label} ${String(p.value)}`).join(", ");
  return (
    <div className="pv-ring">
      <svg
        viewBox="0 0 100 100"
        role="img"
        aria-label={`${String(total)} ${totalLabel}: ${summary}`}
      >
        {/*
         * The track is covered by the parts whenever there is anything to show, and only the hairline gaps between
         * them reveal it, so it paints nothing then and the gaps show the card. An empty ring paints it muted: a
         * near-white track measured 1.09:1 on the card (the surface audit, 2026-10-14), and a mark needs 3:1.
         */}
        <circle
          className={total === 0 ? "pv-ring__track pv-ring__track--empty" : "pv-ring__track"}
          cx="50"
          cy="50"
          r={r}
          fill="none"
          strokeWidth="10"
        />
        {total === 0
          ? null
          : parts.map((p, i) => {
              const length = (p.value / total) * c;
              // A hairline gap between segments, unless the part is the whole ring.
              const gap = parts.length > 1 && length > 2 ? 1.5 : 0;
              const el = (
                <circle
                  key={p.label}
                  cx="50"
                  cy="50"
                  r={r}
                  fill="none"
                  strokeWidth="10"
                  strokeLinecap="butt"
                  style={{ stroke: `var(--pv-part-${String(i % 5)})` }}
                  strokeDasharray={`${String(Math.max(length - gap, 0))} ${String(c)}`}
                  strokeDashoffset={-offset}
                  transform="rotate(-90 50 50)"
                />
              );
              offset += length;
              return el;
            })}
      </svg>
      <div className="pv-ring__center" aria-hidden>
        <strong>{total.toLocaleString(DISPLAY_LOCALE)}</strong>
        <span>{totalLabel}</span>
      </div>
    </div>
  );
}
